import { Eye, EyeOff, Layers, User, X } from 'lucide-react'
import { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import type { GraphExclusionState } from '@/hooks/useGraphExclusion'
import { cn } from '@/utils/cn'
import { LABEL_COLORS } from './cytoscape/entity-icons'

interface ExploreFilterPanelProps {
  nodes: Array<{
    id: string
    name?: string
    typeLabel?: string
    labels?: string[]
  }>
  exclusionState: GraphExclusionState
  onClose: () => void
  includeCaseNodes?: boolean
  onIncludeCaseNodesToggle?: () => void
}

export function ExploreFilterPanel({
  nodes,
  exclusionState,
  onClose,
  includeCaseNodes,
  onIncludeCaseNodesToggle,
}: ExploreFilterPanelProps) {
  const {
    exclusion,
    hideEntityType,
    showEntityType,
    clearHiddenEntityTypes,
    showEntity,
    clearHiddenEntities,
  } = exclusionState

  // Compute node types with counts
  const nodeTypesWithCounts = useMemo(() => {
    const typeCounts = new Map<string, number>()

    nodes.forEach(node => {
      const type = node.typeLabel || node.labels?.[0] || 'Unknown'
      typeCounts.set(type, (typeCounts.get(type) || 0) + 1)
    })

    // Sort by label color order first, then alphabetically
    const order = Object.keys(LABEL_COLORS)
    return Array.from(typeCounts.entries())
      .sort(([a], [b]) => {
        const indexA = order.indexOf(a)
        const indexB = order.indexOf(b)
        const orderA = indexA === -1 ? order.length : indexA
        const orderB = indexB === -1 ? order.length : indexB
        if (orderA !== orderB) return orderA - orderB
        return a.localeCompare(b)
      })
      .map(([type, count]) => ({
        type,
        count,
        isHidden: exclusion.hiddenEntityTypes.includes(type),
        color: LABEL_COLORS[type] || { fill: '#e2e8f0', stroke: '#cbd5e1' },
      }))
  }, [nodes, exclusion.hiddenEntityTypes])

  const hiddenTypesCount = exclusion.hiddenEntityTypes.length
  const hiddenEntitiesCount = exclusion.hiddenEntityIds.length
  const _totalTypes = nodeTypesWithCounts.length
  const totalHiddenCount = hiddenTypesCount + hiddenEntitiesCount

  const handleToggleType = (type: string, isHidden: boolean) => {
    if (isHidden) {
      showEntityType(type)
    } else {
      hideEntityType(type)
    }
  }

  const handleShowAllTypes = () => {
    clearHiddenEntityTypes()
  }

  const handleHideAllTypes = () => {
    nodeTypesWithCounts.forEach(({ type }) => {
      if (!exclusion.hiddenEntityTypes.includes(type)) {
        hideEntityType(type)
      }
    })
  }

  const handleClearAll = () => {
    clearHiddenEntityTypes()
    clearHiddenEntities()
  }

  return (
    <div className="h-full flex flex-col bg-neutral-900/95 backdrop-blur-sm">
      {/* Header */}
      <div className="p-4 border-b border-neutral-700 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-white text-sm">Filters</h3>
          {totalHiddenCount > 0 && (
            <p className="text-xs text-neutral-400 mt-0.5">
              {hiddenTypesCount > 0 && `${hiddenTypesCount} type(s)`}
              {hiddenTypesCount > 0 && hiddenEntitiesCount > 0 && ', '}
              {hiddenEntitiesCount > 0 && `${hiddenEntitiesCount} entity(s)`}
              {' hidden'}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-7 w-7 text-neutral-400 hover:text-white hover:bg-neutral-800 max-lg:h-9 max-lg:w-9"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Case Nodes Toggle Section - Only show if props provided */}
      {includeCaseNodes !== undefined && onIncludeCaseNodesToggle && (
        <div className="px-4 py-3 border-b border-neutral-700 bg-neutral-900/50">
          <div className="flex items-center gap-2">
            <Checkbox
              id="include-case-nodes-filter"
              checked={includeCaseNodes}
              onCheckedChange={onIncludeCaseNodesToggle}
              className="border-neutral-600 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
            />
            <Label
              htmlFor="include-case-nodes-filter"
              className="text-sm text-neutral-200 cursor-pointer flex-1"
            >
              Show case nodes
            </Label>
          </div>
          <p className="text-xs text-neutral-400 mt-1 ml-6">
            Display nodes that are pinned to this case
          </p>
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Node Types Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-medium text-neutral-400 uppercase tracking-wide flex items-center gap-2">
                <Layers className="h-3.5 w-3.5" />
                Node Types
              </h4>
              <Button
                variant="link"
                size="sm"
                onClick={hiddenTypesCount > 0 ? handleShowAllTypes : handleHideAllTypes}
                className="h-auto p-0 text-xs text-blue-400 hover:text-blue-300 max-lg:py-2"
              >
                {hiddenTypesCount > 0 ? 'Show All' : 'Hide All'}
              </Button>
            </div>

            <div className="space-y-1.5">
              {nodeTypesWithCounts.map(({ type, count, isHidden, color }) => (
                <div
                  key={type}
                  className={cn(
                    'flex items-center justify-between py-2 px-3 rounded-lg transition-all cursor-pointer group',
                    isHidden
                      ? 'bg-neutral-800/50 opacity-60'
                      : 'bg-neutral-800 hover:bg-neutral-750'
                  )}
                  onClick={() => handleToggleType(type, isHidden)}
                >
                  <div className="flex items-center gap-3">
                    {/* Color indicator */}
                    <div
                      className={cn(
                        'w-3 h-3 rounded-full border',
                        isHidden && 'opacity-50 grayscale'
                      )}
                      style={{
                        backgroundColor: color.fill,
                        borderColor: color.stroke,
                      }}
                    />
                    {/* Type name */}
                    <span
                      className={cn(
                        'text-sm font-medium',
                        isHidden ? 'text-neutral-500' : 'text-neutral-200'
                      )}
                    >
                      {type}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Count badge */}
                    <span
                      className={cn(
                        'text-xs px-2 py-0.5 rounded-full',
                        isHidden
                          ? 'bg-neutral-700 text-neutral-500'
                          : 'bg-neutral-700 text-neutral-300'
                      )}
                    >
                      {count}
                    </span>
                    {/* Visibility icon */}
                    <div
                      className={cn(
                        'transition-opacity',
                        isHidden
                          ? 'opacity-100'
                          : 'opacity-0 group-hover:opacity-100 max-lg:opacity-100'
                      )}
                    >
                      {isHidden ? (
                        <EyeOff className="h-4 w-4 text-neutral-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-neutral-400" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {nodeTypesWithCounts.length === 0 && (
              <div className="text-center py-8 text-neutral-500 text-sm">
                No node types available
              </div>
            )}
          </div>

          {/* Hidden Entities Section */}
          {hiddenEntitiesCount > 0 && (
            <>
              <Separator className="bg-neutral-700" />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-medium text-neutral-400 uppercase tracking-wide flex items-center gap-2">
                    <User className="h-3.5 w-3.5" />
                    Hidden Entities
                  </h4>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={clearHiddenEntities}
                    className="h-auto p-0 text-xs text-blue-400 hover:text-blue-300 max-lg:py-2"
                  >
                    Show All
                  </Button>
                </div>

                <div className="space-y-1.5">
                  {exclusion.hiddenEntityIds.map(entityId => {
                    const entityName = exclusion.hiddenEntityNames[entityId] || entityId
                    return (
                      <div
                        key={entityId}
                        className="flex items-center justify-between py-2 px-3 rounded-lg bg-neutral-800/50 opacity-80 cursor-pointer hover:opacity-100 transition-opacity group"
                        onClick={() => showEntity(entityId)}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <EyeOff className="h-3.5 w-3.5 text-orange-400 flex-shrink-0" />
                          <span className="text-sm text-neutral-400 truncate">{entityName}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-neutral-400 hover:text-white max-lg:h-8 max-lg:w-8 max-lg:opacity-100"
                          onClick={e => {
                            e.stopPropagation()
                            showEntity(entityId)
                          }}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </ScrollArea>

      {/* Footer with summary */}
      {totalHiddenCount > 0 && (
        <div className="p-4 border-t border-neutral-700">
          <div className="text-xs text-neutral-400 mb-2">
            <span className="text-orange-400 font-medium">{totalHiddenCount} item(s)</span> hidden
            from graph
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearAll}
            className="w-full border-neutral-700 text-neutral-300 hover:bg-neutral-800"
          >
            Clear All Filters
          </Button>
        </div>
      )}
    </div>
  )
}
